import React, { useState, useEffect, useCallback } from 'react';
import {
  Calculator, Check, X, Clock, AlertTriangle, CheckCircle,
  CreditCard, User, Loader2
} from 'lucide-react';

import api from '../../services/api';
import { Toast } from '../ui/Toast';
import ConfirmDialog from '../ConfirmDialog';
import { useLoader } from '../../contexts/LoaderContext';
import EmployeSelector from './shared/EmployeSelector';
import PeriodSelector from './shared/PeriodSelector';
import { useParamsCompta } from '../../contexts/ParametresComptaContext';

// ============================================================================
// PAIE MODULE — Gestion complète de la paie
// ============================================================================
// Fonctionnalités :
//   - Liste des employés avec statut paiement
//   - Calcul automatique du salaire (base - retenues - avances)
//   - Gestion des retenues (avec/sans + justification)
//   - Validation du calcul
//   - Paiement + clôture
// ============================================================================

const TYPES_PRIME = {
  performance: 'Prime de performance',
  assiduite: 'Prime d\'assiduité',
  anciennete: 'Prime d\'ancienneté',
  exceptionnelle: 'Prime exceptionnelle',
  fin_annee: 'Prime de fin d\'année',
  autre: 'Prime'
};

const PaieModule = () => {
  // États principaux
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [employes, setEmployes] = useState([]);
  const [selectedEmploye, setSelectedEmploye] = useState('');
  const [loadingEmployes, setLoadingEmployes] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTermEmploye, setSearchTermEmploye] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: '' });

  // États de validation
  const [calculationValidated, setCalculationValidated] = useState(false);
  const [paymentValidated, setPaymentValidated] = useState(false);
  const [paiementExistant, setPaiementExistant] = useState(null);

  // Modales
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showRetenueModal, setShowRetenueModal] = useState(false);

  // Paiement
  const [selectedModePaiement, setSelectedModePaiement] = useState('');
  const [modesPaiement, setModesPaiement] = useState([]);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentStep, setPaymentStep] = useState('payment');

  const [calculHoraire, setCalculHoraire] = useState(null);
  const [loadingCalculHoraire, setLoadingCalculHoraire] = useState(false);

  const [dernierCalcul, setDernierCalcul] = useState(null);

  // Retenue
  const [retenueAbsence, setRetenueAbsence] = useState('');
  const [typeRetenue, setTypeRetenue] = useState('avec');
  const [justificationRetenue, setJustificationRetenue] = useState('');

  const { showLoader, hideLoader } = useLoader();
  const { formatMontant, symbole, getNumber, params } = useParamsCompta();

  // Toast
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => {
        setToast({ show: false, message: '', type: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  // ============================================================================
  // CHARGEMENT DES DONNÉES
  // ============================================================================
  useEffect(() => {
    loadEmployesData();
    loadModesPaiement();
  }, [selectedDate]);

  const loadModesPaiement = async () => {
    try {
      const response = await api.modesPaiement.getAll();
      if (response.success) {
        setModesPaiement(response.data);
      }
    } catch (error) {
      console.error('Erreur chargement modes paiement:', error);
    }
  };


  const chargerCalculHoraire = async (employeId) => {
    setLoadingCalculHoraire(true);
    setCalculHoraire(null);
    try {
      const mois = selectedDate.getMonth() + 1;
      const annee = selectedDate.getFullYear();
      const result = await api.pointages.calculerSalaireHoraire(employeId, mois, annee);
      if (result.success) {
        setCalculHoraire(result.data);
      } else {
        showToast(result.message || 'Erreur calcul horaire', 'error');
      }
    } catch (e) {
      console.error('Erreur calcul horaire:', e);
      showToast('Erreur lors du calcul horaire', 'error');
    } finally {
      setLoadingCalculHoraire(false);
    }
  };



  const loadEmployesData = async () => {
    showLoader();
    setLoadingEmployes(true);
    try {
      const mois = selectedDate.getMonth() + 1;
      const annee = selectedDate.getFullYear();

      const response = await api.comptabilite.getPointagesMensuelsEmployes(mois, annee);

      if (response.success) {
        const employesAvecPaiements = await Promise.all(
          response.data.map(async (employe) => {
            try {
              const paiementResponse = await api.paiementsSalaires.verifierPaiementEmployeExistant(
                employe.id, mois, annee
              );

              return {
                ...employe,
                paiement_existe: paiementResponse.success && paiementResponse.existe,
                paiement_info: paiementResponse.paiement || null,
                avances_mois: paiementResponse.avances || [],
                montant_avances_total: paiementResponse.montant_avances || 0,
                primes_mois: paiementResponse.primes || [],
                montant_primes_total: paiementResponse.montant_primes || 0,
              };


            } catch (error) {
              console.error(`Erreur vérification paiement employé ${employe.id}:`, error);
              return { ...employe, paiement_existe: false, paiement_info: null };
            }
          })
        );

        setEmployes(employesAvecPaiements);
      } else {
        showToast(response.message || 'Erreur lors du chargement', 'error');
      }
    } catch (error) {
      console.error('Erreur chargement employés:', error);
      showToast('Erreur lors du chargement des employés', 'error');
    } finally {
      setLoadingEmployes(false);
      hideLoader();
    }
  };

  // ============================================================================
  // CALCUL RÉMUNÉRATION
  // ============================================================================
  const tauxCnss = getNumber('taux_cnss', 0);




  const calculerRemuneration = (employe) => {
    if (!employe || !employe.presences) {
      return {
        salaire_base: 0, total_retenues: 0, total_avances: 0,
        salaire_net: 0, jours_ouvres_attendus: 0, jours_pointes: 0,
        jours_manquants: 0, peut_payer: false, message_erreur: null,
        montant_cnss: 0, taux_cnss: 0, is_horaire: false
      };
    }

    // ── EMPLOYÉ HORAIRE ──────────────────────────────────────────────────
    if (employe.salary_type === 'hourly') {
      if (!calculHoraire) {
        return {
          salaire_base: 0, total_retenues: 0, total_avances: 0,
          salaire_net: 0, jours_ouvres_attendus: 0, jours_pointes: 0,
          jours_manquants: 0, peut_payer: false,
          message_erreur: 'Calcul en cours...', montant_cnss: 0, taux_cnss: 0,
          is_horaire: true
        };
      }

      const totalAvances = employe.montant_avances_total || 0;
      const totalPrimes = employe.montant_primes_total || 0;
      const retenueGlobale = employe.retenue_temp ? parseFloat(employe.retenue_temp) : 0;
      const montantCnssHoraire = Math.round(calculHoraire.resume.total_brut * tauxCnss / 100);
      const totalRetenuesHoraire = calculHoraire.resume.total_deductions + retenueGlobale + montantCnssHoraire;
      const salaireNetHoraire = Math.max(0, calculHoraire.resume.total_brut - totalRetenuesHoraire + totalPrimes - totalAvances);


      return {
        salaire_base: calculHoraire.resume.total_brut,
        montant_cnss: montantCnssHoraire,
        taux_cnss: tauxCnss,
        total_retenues: totalRetenuesHoraire,
        retenue_ponctualite: calculHoraire.resume.total_deductions,
        retenue_globale: retenueGlobale,
        total_avances: totalAvances,
        total_primes: totalPrimes,
        primes_mois: employe.primes_mois || [],
        salaire_net: salaireNetHoraire,
        jours_ouvres_attendus: calculHoraire.resume.jours_presents + calculHoraire.resume.jours_justifies,
        jours_pointes: calculHoraire.resume.jours_presents,
        jours_manquants: calculHoraire.resume.jours_incomplets,
        total_heures: calculHoraire.resume.total_heures,
        taux_horaire: calculHoraire.employee.taux_horaire,
        peut_payer: calculHoraire.resume.jours_incomplets === 0,
        message_erreur: calculHoraire.resume.jours_incomplets > 0
          ? `${calculHoraire.resume.jours_incomplets} jour(s) incomplet(s) non justifié(s)`
          : null,
        is_horaire: true,
        detail_jours: calculHoraire.detail_jours
      };
    }

    // ── EMPLOYÉ MENSUEL FIXE (flux inchangé) ─────────────────────────────
    const mois = selectedDate.getMonth();
    const annee = selectedDate.getFullYear();
    const joursOuvres = [];

    const premierJour = new Date(annee, mois, 1, 12, 0, 0);
    const dernierJour = new Date(annee, mois + 1, 0, 12, 0, 0);

    for (let jour = new Date(premierJour); jour <= dernierJour; jour.setDate(jour.getDate() + 1)) {
      const dateStr = `${jour.getFullYear()}-${String(jour.getMonth() + 1).padStart(2, '0')}-${String(jour.getDate()).padStart(2, '0')}`;
      joursOuvres.push(dateStr);
    }

    const datesPointees = employe.presences.map(p => {
      if (!p.date) return null;
      if (typeof p.date === 'string' && p.date.includes('T')) return p.date.split('T')[0];
      return p.date;
    }).filter(Boolean);

    const joursManquants = joursOuvres.filter(jour => !datesPointees.includes(jour));

    let totalRetenuesPresences = 0;
    employe.presences.forEach(p => {
      if (p.retenue_absence && p.retenue_absence > 0) {
        totalRetenuesPresences += parseFloat(p.retenue_absence);
      }
    });

    let retenueGlobale = 0;
    if (employe.retenue_temp && employe.retenue_temp > 0) {
      retenueGlobale = parseFloat(employe.retenue_temp);
    }

    const totalRetenues = totalRetenuesPresences + retenueGlobale;

    let totalAvances = 0;
    if (employe.avances_mois && Array.isArray(employe.avances_mois)) {
      totalAvances = employe.avances_mois.reduce((sum, avance) => {
        if (avance.statut === 'validee' || avance.statut === 'appliquee') {
          return sum + parseFloat(avance.montant || 0);
        }
        return sum;
      }, 0);
    }

    const salaireBase = parseFloat(employe.salaire_fixe) || 0;
    const totalPrimes = employe.montant_primes_total || 0;
    const montantCnss = Math.round(salaireBase * tauxCnss / 100);
    const salaireNet = salaireBase - montantCnss - totalRetenues - totalAvances + totalPrimes;

    return {
      salaire_base: salaireBase,
      taux_cnss: tauxCnss,
      montant_cnss: montantCnss,
      total_retenues: totalRetenues,
      total_retenues_presences: totalRetenuesPresences,
      retenue_globale: retenueGlobale,
      total_avances: totalAvances,
      total_primes: totalPrimes,
      primes_mois: employe.primes_mois || [],
      salaire_net: salaireNet,
      jours_ouvres_attendus: joursOuvres.length,
      jours_pointes: datesPointees.length,
      jours_manquants: joursManquants.length,
      dates_manquantes: joursManquants,
      peut_payer: joursManquants.length === 0,
      message_erreur: joursManquants.length > 0
        ? `${joursManquants.length} jour(s) sans pointage`
        : null,
      is_horaire: false
    };
  };



  // ============================================================================
  // RETENUE
  // ============================================================================
  const handleSaveRetenue = async () => {
    if (typeRetenue === 'avec' && !retenueAbsence) {
      showToast('Veuillez saisir le montant de la retenue', 'error');
      return;
    }
    if (!justificationRetenue.trim()) {
      showToast('Veuillez saisir une justification', 'error');
      return;
    }

    try {
      const montant = typeRetenue === 'avec' ? parseFloat(retenueAbsence) : 0;

      setEmployes(prev => prev.map(emp => {
        if (emp.id.toString() === selectedEmploye.toString()) {
          return {
            ...emp,
            retenue_temp: montant,
            justification_retenue_temp: justificationRetenue.trim(),
          };
        }
        return emp;
      }));

      setShowRetenueModal(false);
      setRetenueAbsence('');
      setJustificationRetenue('');
      setTypeRetenue('avec');
      setCalculationValidated(false);

      if (typeRetenue === 'avec') {
        showToast(`Retenue de ${formatMontant(montant)} enregistrée`, 'success');
      } else {
        showToast('Absence de retenue enregistrée', 'success');
      }
    } catch (error) {
      console.error('Erreur enregistrement retenue:', error);
      showToast('Erreur lors de l\'enregistrement', 'error');
    }
  };

  // ============================================================================
  // VALIDATION
  // ============================================================================
  const handleValidateCalculation = async () => {
    try {
      setShowValidationDialog(false);
      setLoading(true);
      await new Promise(resolve => setTimeout(resolve, 2000));
      setCalculationValidated(true);
      showToast('Calcul validé - Vous pouvez maintenant effectuer le paiement', 'success');
    } catch (error) {
      console.error('Erreur validation:', error);
      showToast('Erreur lors de la validation', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // PAIEMENT
  // ============================================================================
  const handleProcessPayment = async () => {
    if (!selectedModePaiement) {
      showToast('Veuillez sélectionner un mode de paiement', 'error');
      return;
    }

    setShowPaymentDialog(false);
    setIsProcessingPayment(true);
    setPaymentStep('payment');

    try {
      const mois = selectedDate.getMonth() + 1;
      const annee = selectedDate.getFullYear();
      const employe = employes.find(e => e.id.toString() === selectedEmploye);

      if (!employe) {
        showToast('Employé non trouvé', 'error');
        setIsProcessingPayment(false);
        return;
      }

      // Recharger les avances et primes 
      const verificationResponse = await api.paiementsSalaires.verifierPaiementEmployeExistant(
        selectedEmploye, mois, annee
      );

      const employeAvecPrimes = {
        ...employe,
        avances_mois: verificationResponse.avances || [],
        montant_avances_total: verificationResponse.montant_avances || 0,
        primes_mois: verificationResponse.primes || [],
        montant_primes_total: verificationResponse.montant_primes || 0,
      };

      const calcul = calculerRemuneration(employeAvecPrimes);


      const payloadPaiement = {
        employe_id: parseInt(selectedEmploye),
        mois, annee,
        montant_brut: calcul.salaire_base,

        montant_retenues: calcul.total_retenues,

        justification_retenue: calcul.is_horaire
          ? `Salaire horaire — ${calcul.total_heures}h × ${formatMontant(calcul.taux_horaire)}/h`
          : employe.justification_retenue_temp || null,
        montant_avances: calcul.total_avances,
        montant_net: calcul.salaire_net,
        mode_paiement_id: parseInt(selectedModePaiement),
        jours_ouvres: calcul.jours_ouvres_attendus,
        jours_pointes: calcul.jours_pointes,
        jours_manquants: calcul.jours_manquants,
        // Nouveaux champs détail horaire
        type_salaire: employe.salary_type || 'monthly',
        total_heures: calcul.is_horaire ? calcul.total_heures : null,
        taux_horaire: calcul.is_horaire ? calcul.taux_horaire : null,
        retenue_ponctualite: calcul.is_horaire ? calcul.retenue_ponctualite : 0,
        retenue_cnss: calcul.montant_cnss || 0,
        montant_primes: calcul.total_primes || 0,
        jours_conge_paye: calcul.jours_conge_payes || 0,
        jours_conge_non_paye: calcul.jours_conge_non_payes || 0,
      };



      console.log('💰 Payload primes:', {
        montant_primes: calcul.total_primes,
        employe_primes_total: employe.montant_primes_total,
        payload_montant_primes: payloadPaiement.montant_primes
      });

      const response = await api.paiementsSalaires.creerPaiementEmploye(payloadPaiement);

      if (!response.success) {
        showToast(response.message || 'Erreur lors du paiement', 'error');
        setIsProcessingPayment(false);
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 3000));

      // Clôturer
      setPaymentStep('cloture');
      const clotureResponse = await api.paiementsSalaires.cloturerPaiementEmploye(response.data.id);

      if (!clotureResponse.success) {
        showToast('Paiement effectué mais échec de la clôture', 'warning');
        setPaiementExistant(response.data);
        setDernierCalcul(calcul);
        setPaymentValidated(true);
        setCalculationValidated(true);
        setIsProcessingPayment(false);
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 2000));

      // Recharger
      await loadEmployesData();

      const paiementRefresh = await api.paiementsSalaires.verifierPaiementEmployeExistant(
        selectedEmploye, mois, annee
      );

      if (paiementRefresh.success && paiementRefresh.existe) {
        setPaiementExistant(paiementRefresh.paiement);
      } else {
        setPaiementExistant({
          ...response.data,
          statut: 'cloture',
          date_cloture: new Date().toISOString(),
          montant_brut: calcul.salaire_base,
          montant_retenues: calcul.total_retenues,
          montant_avances: calcul.total_avances,
          montant_net: calcul.salaire_net,
        });
      }

      setPaymentValidated(true);
      setCalculationValidated(true);
      showToast('Paiement clôturé avec succès', 'success');

    } catch (error) {
      console.error('Erreur traitement paiement:', error);
      showToast('Erreur lors du traitement du paiement: ' + error.message, 'error');
    } finally {
      setIsProcessingPayment(false);
      setPaymentStep('payment');
    }
  };

  // ============================================================================
  // SÉLECTION EMPLOYÉ
  // ============================================================================
  const handleSelectEmploye = (employe) => {
    setSelectedEmploye(employe.id.toString());
    setCalculHoraire(null);

    if (employe.paiement_existe && employe.paiement_info) {
      setPaiementExistant(employe.paiement_info);
      setPaymentValidated(true);
      setCalculationValidated(true);
    } else {
      setPaiementExistant(null);
      setPaymentValidated(false);
      setCalculationValidated(false);
      // Charger le calcul horaire si besoin
      if (employe.salary_type === 'hourly') {
        chargerCalculHoraire(employe.id);
      }
    }
  };

  // Employé sélectionné et son calcul
  const employeActuel = employes.find(e => e.id.toString() === selectedEmploye);
  const calcul = employeActuel ? calculerRemuneration(employeActuel) : null;

  // ============================================================================
  // RENDU
  // ============================================================================
  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#fdfcf5' }}>
      {/* Sélecteur de période */}
      <PeriodSelector selectedDate={selectedDate} onChange={setSelectedDate} />

      {/* Grille principale */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        {/* Colonne 1 : Liste employés */}
        <div className="xl:col-span-3">
          <EmployeSelector
            employes={employes}
            selectedEmploye={selectedEmploye}
            onSelect={handleSelectEmploye}
            searchTerm={searchTermEmploye}
            onSearchChange={setSearchTermEmploye}
            loading={loadingEmployes}
            color="primary"


            renderBadge={(employe) => (
              <div className="flex items-center gap-1 flex-wrap justify-end">
                <span className={`text-xs px-2 py-1 font-semibold rounded-lg ${employe.salary_type === 'hourly'
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-blue-100 text-blue-700'
                  }`}>
                  {employe.salary_type === 'hourly' ? 'Horaire' : 'Mensuel'}
                </span>
                {employe.paiement_existe && (
                  <span className="text-xs px-2 py-1 font-semibold rounded-lg bg-green-100 text-green-700">
                    Payé
                  </span>
                )}
              </div>
            )}


          />
        </div>

        {/* Colonne 2 : Calcul du salaire */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 xl:col-span-6">
          <div className="px-6 py-4 mb-5 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#448696' }}>
            <Calculator size={24} className="text-white" />
            <h2 className="font-bold text-white text-lg">Calcul du salaire</h2>
          </div>

          {!selectedEmploye ? (
            <div className="text-center py-16 text-gray-400">
              <Calculator className="h-20 w-20 mx-auto mb-4 opacity-50" />
              <h4 className="text-lg font-bold text-gray-700 mb-2">Aucun employé sélectionné</h4>
              <p className="text-sm font-medium">Sélectionnez un employé dans la liste</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Résumé employé */}
              <div className="p-4 rounded-xl border-2" style={{ backgroundColor: '#f0f9fb', borderColor: '#448696' }}>
                {params.jour_paie && (
                  <div className="text-xs font-semibold text-gray-400 mb-2">
                    Jour de paie : le {params.jour_paie} du mois
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-bold text-gray-900">{employeActuel.prenom} {employeActuel.nom}</div>
                    <div className="text-sm font-medium text-gray-600 mt-1">{employeActuel.role} - {employeActuel.departement}</div>
                  </div>



                  <div className="text-right">
                    {employeActuel.salary_type === 'hourly' ? (
                      <>
                        <div className="text-lg font-bold" style={{ color: '#448696' }}>
                          {formatMontant(employeActuel.taux_horaire_personnalise || employeActuel.groupe_taux_horaire || 0)}/h
                        </div>
                        <div className="text-xs font-medium text-gray-600">
                          {employeActuel.groupe_nom || 'Taux personnalisé'}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-lg font-bold" style={{ color: '#448696' }}>{formatMontant(employeActuel.salaire_fixe || 0)}</div>
                        <div className="text-xs font-medium text-gray-600">Salaire base</div>
                      </>
                    )}
                  </div>

                </div>
              </div>

              {/* Stats présences */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl text-center border-2" style={{ backgroundColor: '#d4f4dd', borderColor: '#22c55e' }}>
                  <div className="text-2xl font-bold text-green-700">{employeActuel.nombre_presences || 0}</div>
                  <div className="text-xs font-semibold text-green-700 mt-1">Jours travaillés</div>
                </div>
                <div className="p-4 rounded-xl text-center border-2" style={{ backgroundColor: '#fee2e2', borderColor: '#ef4444' }}>
                  <div className="text-2xl font-bold text-red-700">{employeActuel.nombre_absences || 0}</div>
                  <div className="text-xs font-semibold text-red-700 mt-1">Absences</div>
                </div>
              </div>



              {/* Calcul détaillé */}
              <div className="p-5 rounded-xl" style={{ backgroundColor: '#f5f3eb' }}>
                <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center">
                  <Calculator className="w-4 h-4 mr-2" />
                  Détail du calcul
                </h4>

                {paymentValidated && paiementExistant ? (
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between font-semibold">
                      <span className="text-gray-700">Salaire brut :</span>
                      <span className="text-gray-900">{formatMontant(paiementExistant.montant_brut)}</span>
                    </div>
                    {parseFloat(paiementExistant.montant_retenues) > 0 && (
                      <div className="flex justify-between text-red-600 font-semibold">
                        <span>Retenues :</span>
                        <span>- {formatMontant(paiementExistant.montant_retenues)}</span>
                      </div>
                    )}

                    {parseFloat(paiementExistant.montant_avances || 0) > 0 && (
                      <div className="flex justify-between text-orange-600 font-semibold">
                        <span>Avances :</span>
                        <span>- {formatMontant(paiementExistant.montant_avances)}</span>
                      </div>
                    )}
                    {parseFloat(paiementExistant.montant_primes || 0) > 0 && (
                      <div className="flex justify-between text-green-600 font-semibold">
                        <span>Primes :</span>
                        <span>+ {formatMontant(paiementExistant.montant_primes)}</span>
                      </div>
                    )}

                    <div className="flex justify-between text-xl font-bold pt-3 border-t-2"
                      style={{ color: '#448696', borderColor: '#448696' }}>
                      <span>Salaire net :</span>
                      <span>{formatMontant(paiementExistant.montant_net)}</span>
                    </div>
                    <div className="mt-3 p-3 rounded-xl text-xs text-center font-medium"
                      style={{ backgroundColor: '#f0f9fb', color: '#448696', border: '1px solid #c8dde2' }}>
                      Consultez le registre comptable pour le détail complet de ce paiement
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between font-semibold">
                        <span className="text-gray-700">Salaire de base :</span>
                        <span className="text-gray-900">{formatMontant(calcul.salaire_base)}</span>
                      </div>

                      {/* Bloc horaire — détail heures */}
                      {calcul.is_horaire && (
                        <div className="pt-3 border-t-2 border-gray-200">
                          {loadingCalculHoraire ? (
                            <div className="flex items-center gap-2 text-gray-400 text-xs">
                              <Loader2 size={14} className="animate-spin" />
                              Calcul en cours...
                            </div>
                          ) : calculHoraire ? (
                            <div className="space-y-1.5 text-xs text-gray-600">
                              <div className="flex justify-between">
                                <span>Heures travaillées :</span>
                                <span className="font-bold text-gray-900">{calcul.total_heures}h</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Taux horaire :</span>
                                <span className="font-bold text-gray-900">{formatMontant(calcul.taux_horaire)}/h</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Jours présents :</span>
                                <span className="font-bold text-green-700">{calcul.jours_pointes}j</span>
                              </div>
                              {calcul.jours_ouvres_attendus - calcul.jours_pointes > 0 && (
                                <div className="flex justify-between">
                                  <span>Jours justifiés :</span>
                                  <span className="font-bold text-blue-600">{calcul.jours_ouvres_attendus - calcul.jours_pointes}j</span>
                                </div>
                              )}

                              {calcul.jours_manquants > 0 && (
                                <div className="flex justify-between text-orange-600">
                                  <span>Jours incomplets bloqués :</span>
                                  <span className="font-bold">{calcul.jours_manquants}j</span>
                                </div>
                              )}
                              {calculHoraire?.resume?.jours_conge_non_paye > 0 && (
                                <div className="flex justify-between text-gray-500">
                                  <span>Congés non payés :</span>
                                  <span className="font-bold">{calculHoraire.resume.jours_conge_non_paye}j — 0h</span>
                                </div>
                              )}
                              {calculHoraire?.resume?.jours_justifies > 0 && (
                                <div className="flex justify-between text-blue-500">
                                  <span>Jours justifiés / congés payés :</span>
                                  <span className="font-bold">{calculHoraire.resume.jours_justifies}j</span>
                                </div>
                              )}


                              {calcul.retenue_ponctualite > 0 && (
                                <div className="flex justify-between text-red-600 pt-1 border-t border-gray-100">
                                  <span>Déductions ponctualité :</span>
                                  <span className="font-bold">- {formatMontant(calcul.retenue_ponctualite)}</span>
                                </div>
                              )}
                              {calcul.montant_cnss > 0 && (
                                <div className="flex justify-between text-blue-600 pt-1 border-t border-gray-100">
                                  <span>{params.nom_cotisation_sociale || 'CNSS'} ({calcul.taux_cnss}%) :</span>
                                  <span className="font-bold">- {formatMontant(calcul.montant_cnss)}</span>
                                </div>
                              )}
                            </div>
                          ) : null}
                        </div>
                      )}

                      {/* CNSS mensuel fixe */}
                      {calcul.montant_cnss > 0 && !calcul.is_horaire && (
                        <div className="pt-3 border-t-2 border-gray-200">
                          <div className="flex justify-between text-blue-600 font-semibold">
                            <span>{params.nom_cotisation_sociale || 'CNSS'} ({calcul.taux_cnss}%) :</span>
                            <span>- {formatMontant(calcul.montant_cnss)}</span>
                          </div>
                        </div>
                      )}

                      {/* Retenues manuelles */}
                      {((calcul.total_retenues > 0 && calcul.is_horaire && calcul.retenue_globale > 0) ||
                        (calcul.total_retenues > 0 && !calcul.is_horaire)) && (
                          <div className="pt-3 border-t-2 border-gray-200">
                            <div className="flex justify-between text-red-600 font-semibold">
                              <span>Retenues :</span>
                              <span>- {formatMontant(calcul.is_horaire ? calcul.retenue_globale : calcul.total_retenues, false)}</span>
                            </div>
                          </div>
                        )}


                      {/* Avances */}
                      {calcul.total_avances > 0 && (
                        <div className="flex justify-between text-orange-600 font-semibold">
                          <span>Avances :</span>
                          <span>- {formatMontant(calcul.total_avances, false)}</span>
                        </div>
                      )}

                      {/* Primes */}
                      {calcul.total_primes > 0 && (
                        <div className="pt-2 border-t border-gray-100">
                          <div className="flex justify-between text-green-600 font-semibold">
                            <span>Primes :</span>
                            <span>+ {formatMontant(calcul.total_primes, false)}</span>
                          </div>
                          {calcul.primes_mois && calcul.primes_mois.length > 0 && (
                            <div className="mt-1 space-y-0.5">
                              {calcul.primes_mois.map(p => (
                                <div key={p.id} className="flex justify-between text-xs text-green-500 pl-2">
                                  <span>{TYPES_PRIME[p.type_prime] || p.type_prime}{p.motif ? ` — ${p.motif}` : ''}</span>
                                  <span>+ {formatMontant(p.montant, false)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}


                      {/* Salaire net */}
                      <div className="flex justify-between text-xl font-bold pt-3 border-t-2"
                        style={{ color: '#448696', borderColor: '#448696' }}>
                        <span>Salaire net :</span>
                        <span>{formatMontant(calcul.salaire_net)}</span>
                      </div>
                    </div>
                  </>
                )}

                {/* Bouton retenue */}
                <div className="mt-5 pt-5 border-t-2 border-gray-200">
                  <button
                    onClick={() => {
                      if (!paymentValidated) {
                        setRetenueAbsence('');
                        setTypeRetenue('avec');
                        setJustificationRetenue('');
                        setShowRetenueModal(true);
                      }
                    }}
                    disabled={paymentValidated}
                    className={`w-full px-4 py-3 text-sm text-gray-900 rounded-xl font-semibold transition-all shadow-sm hover:shadow-md ${paymentValidated ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    style={{ backgroundColor: '#fed774' }}
                    onMouseEnter={(e) => { if (!paymentValidated) { e.currentTarget.style.backgroundColor = '#fdd24e'; e.currentTarget.style.transform = 'translateY(-2px)'; } }}
                    onMouseLeave={(e) => { if (!paymentValidated) { e.currentTarget.style.backgroundColor = '#fed774'; e.currentTarget.style.transform = 'translateY(0)'; } }}
                  >
                    <CreditCard className="w-4 h-4 inline mr-2" />
                    Ajouter une retenue
                  </button>
                </div>

              </div>



              {/* Bouton validation */}
              {calcul?.is_horaire && calcul?.jours_manquants > 0 && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-xs font-semibold"
                  style={{ backgroundColor: '#fff7ed', border: '1px solid #fdba74', color: '#c2410c' }}>
                  <AlertTriangle size={14} className="flex-shrink-0" />
                  {calcul.jours_manquants} jour(s) incomplet(s) non justifié(s) - justifiez-les dans l'historique de pointage pour débloquer la validation
                </div>
              )}
              <button
                onClick={() => setShowValidationDialog(true)}
                disabled={calculationValidated || paymentValidated || loading || (calcul?.is_horaire && calcul?.jours_manquants > 0)}


                className={`w-full py-4 px-4 rounded-xl font-semibold text-sm transition-all shadow-lg hover:shadow-xl ${calculationValidated || paymentValidated || loading || (calcul?.is_horaire && calcul?.jours_manquants > 0) ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                style={{ backgroundColor: calculationValidated ? '#22c55e' : '#448696', color: '#fff' }}
                onMouseEnter={(e) => { if (!calculationValidated && !paymentValidated && !loading) { e.currentTarget.style.backgroundColor = '#6fa3b0'; e.currentTarget.style.transform = 'translateY(-2px)'; } }}
                onMouseLeave={(e) => { if (!calculationValidated && !paymentValidated && !loading) { e.currentTarget.style.backgroundColor = '#448696'; e.currentTarget.style.transform = 'translateY(0)'; } }}
              >
                {loading ? (
                  <><Loader2 className="w-5 h-5 inline mr-2 animate-spin" />Validation...</>
                ) : calculationValidated ? (
                  <><Check className="w-5 h-5 inline mr-2" />Calcul validé</>
                ) : (
                  'Valider le calcul'
                )}
              </button>
            </div>
          )}
        </div>

        {/* Colonne 3 : Paiement */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 xl:col-span-3">
          <div className="px-6 py-4 mb-5 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#fed774' }}>
            <CreditCard size={24} className="text-gray-900" />
            <h2 className="font-bold text-gray-900 text-lg">Paiement</h2>
          </div>

          {!calculationValidated ? (
            <div className="text-center py-12 text-gray-400">
              <CreditCard className="h-16 w-16 mx-auto mb-3 opacity-50" />
              <p className="text-xs font-medium">Validez le calcul d'abord</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-center p-5 rounded-xl border-2" style={{ backgroundColor: '#d4f4dd', borderColor: '#22c55e' }}>
                <div className="text-xs font-semibold mb-2 text-green-700">
                  {paymentValidated ? 'Montant payé' : 'Montant à payer'}
                </div>
                <div className="text-3xl font-bold text-green-700">
                  {formatMontant(
                    paymentValidated && paiementExistant
                      ? paiementExistant.montant_net
                      : calcul ? calcul.salaire_net : 0
                  )}
                </div>
              </div>


              {!paymentValidated ? (
                <button
                  onClick={() => setShowPaymentDialog(true)}
                  className="w-full py-3 px-4 text-white rounded-xl font-semibold text-sm transition-all shadow-lg hover:shadow-xl"
                  style={{ backgroundColor: '#448696' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#6fa3b0'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#448696'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <CreditCard className="w-4 h-4 inline mr-2" />
                  Effectuer paiement
                </button>
              ) : (
                <div className="p-4 text-center rounded-xl border-2" style={{ backgroundColor: '#22c55e', borderColor: '#22c55e' }}>
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-white" />
                  <div className="font-bold text-white">Paiement clôturé</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ================================================================== */}
      {/* MODALES                                                           */}
      {/* ================================================================== */}

      {/* Modal Retenue */}
      {showRetenueModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="px-6 py-4 mb-6 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#fed774' }}>
              <AlertTriangle size={24} className="text-gray-900" />
              <h3 className="text-xl font-bold text-gray-900">Gérer retenue</h3>
            </div>

            <div className="mb-4 flex gap-3">
              <button
                onClick={() => setTypeRetenue('avec')}
                className={`flex-1 p-3 rounded-xl font-semibold text-sm transition-all border-2 ${typeRetenue === 'avec'
                    ? 'bg-red-50 border-red-500 text-red-700 shadow-sm'
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
              >
                Avec retenue
              </button>
              <button
                onClick={() => setTypeRetenue('sans')}
                className={`flex-1 p-3 rounded-xl font-semibold text-sm transition-all border-2 ${typeRetenue === 'sans'
                    ? 'bg-green-50 border-green-500 text-green-700 shadow-sm'
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
              >
                Sans retenue
              </button>
            </div>

            {typeRetenue === 'avec' && (
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">{`Montant (${symbole}) *`}</label>
                <input
                  type="number"
                  value={retenueAbsence}
                  onChange={(e) => setRetenueAbsence(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none transition-all"
                  onFocus={(e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(136, 185, 197, 0.1)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
            )}

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Justification *</label>
              <textarea
                value={justificationRetenue}
                onChange={(e) => setJustificationRetenue(e.target.value)}
                rows="4"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none transition-all"
                onFocus={(e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(136, 185, 197, 0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }}
              />
            </div>

            <div className="flex gap-3">
              <button onClick={() => setShowRetenueModal(false)} className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">Annuler</button>
              <button onClick={handleSaveRetenue} className="flex-1 px-4 py-3 text-white rounded-xl font-semibold text-sm shadow-lg hover:shadow-xl transition-all" style={{ backgroundColor: '#448696' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#6fa3b0'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#448696'}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Validation */}
      {showValidationDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="px-6 py-4 mb-6 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#448696' }}>
              <CheckCircle size={24} className="text-white" />
              <h3 className="text-xl font-bold text-white">Valider calcul</h3>
            </div>
            <p className="mb-6 font-medium text-gray-700">Confirmez-vous la validation de ce calcul de rémunération ?</p>
            <div className="flex gap-3">
              <button onClick={() => setShowValidationDialog(false)} className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">Annuler</button>
              <button onClick={handleValidateCalculation} className="flex-1 px-4 py-3 text-white rounded-xl font-semibold text-sm shadow-lg hover:shadow-xl transition-all" style={{ backgroundColor: '#448696' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#6fa3b0'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#448696'}>Valider</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Paiement */}
      {showPaymentDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="px-6 py-4 mb-6 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#fed774' }}>
              <CreditCard size={24} className="text-gray-900" />
              <h3 className="text-xl font-bold text-gray-900">Effectuer paiement</h3>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Mode de paiement *</label>
              <select
                value={selectedModePaiement}
                onChange={(e) => setSelectedModePaiement(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none transition-all"
                onFocus={(e) => { e.target.style.borderColor = '#fed774'; e.target.style.boxShadow = '0 0 0 3px rgba(254, 215, 116, 0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }}
              >
                <option value="">Sélectionner...</option>
                {modesPaiement.map(mode => (
                  <option key={mode.id} value={mode.id}>{mode.nom}</option>
                ))}
              </select>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setShowPaymentDialog(false)} className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">Annuler</button>
              <button onClick={handleProcessPayment} className="flex-1 px-4 py-3 text-white rounded-xl font-semibold text-sm shadow-lg hover:shadow-xl transition-all" style={{ backgroundColor: '#448696' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#6fa3b0'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#448696'}>Confirmer</button>
            </div>
          </div>
        </div>
      )}

      {/* Processing Payment */}
      {isProcessingPayment && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10001]">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 animate-pulse">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center shadow-lg"
                style={{ backgroundColor: paymentStep === 'cloture' ? '#22c55e' : '#448696' }}>
                {paymentStep === 'cloture' ? <Check className="text-white" size={40} /> : <CreditCard className="text-white" size={40} />}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {paymentStep === 'cloture' ? 'Clôture paiement' : 'Traitement paiement'}
              </h3>
              <p className="text-sm font-medium text-gray-600">
                {paymentStep === 'cloture' ? 'Finalisation...' : 'Enregistrement...'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast.show && (
        <Toast
          show={toast.show}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: '' })}
        />
      )}
    </div>
  );
};

export default PaieModule;